/**
 * Navigation model — the single source of truth for the sidebar and the
 * router. Each {@link NavItem} maps a route id to a lazily-loaded {@link View}
 * factory, an icon, a page title, and optional permission gating.
 *
 * The five sections mirror the redesigned information architecture:
 *   Fleet Ops · Data · Shuttle · Administration · Developer
 * @module app/nav
 */
import type { ViewFactory } from "./router.ts";

export interface NavItem {
	/** Stable route id, e.g. "fleet/overview". */
	id: string;
	/** Sidebar label and default page title. */
	label: string;
	/** Inline SVG markup for the sidebar icon. */
	icon: string;
	/** Lazy view factory. */
	view: ViewFactory;
	/** Gating by user permission. */
	permission?: string;
	/** When true, the item carries the live alert-count badge. */
	alertBadge?: boolean;
	/** Marks developer-only tooling (styled distinctly). */
	dev?: boolean;
	/** Restriction for superadmin role only. */
	superadminOnly?: boolean;
}

export interface NavSection {
	label: string;
	items: NavItem[];
	/** When set, the whole section is hidden unless the tenant has this capability. */
	capability?: string;
}

const I = {
	grid: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>',
	map: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>',
	truck:
		'<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 17h2m10 0h2M2 9l2-6h16l2 6M2 9h20v8H2V9z"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/></svg>',
	bell: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>',
	users:
		'<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>',
	battery:
		'<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="6" width="18" height="12" rx="2" ry="2"/><line x1="23" y1="13" x2="23" y2="11"/><line x1="11" y1="9" x2="8" y2="12"/><line x1="8" y1="12" x2="11" y2="15"/></svg>',
	activity:
		'<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>',
	shuttle:
		'<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>',
	cog: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>',
	fence:
		'<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>',
	building:
		'<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 21V9h6v12"/><line x1="3" y1="9" x2="21" y2="9"/></svg>',
	report:
		'<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="13" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/></svg>',
	play: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>',
	lock: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>',
};

/** The full navigation tree. Items lazily import their feature modules. */
export const NAV: NavSection[] = [
	{
		label: "Fleet Ops",
		capability: "fleet",
		items: [
			{
				id: "fleet/overview",
				label: "Fleet Dashboard",
				icon: I.grid,
				permission: "fleet:read",
				view: () =>
					import("../features/new-dashboard/fleet-dashboard.ts").then(
						(m) => new m.FleetDashboardView(),
					),
			},
			{
				id: "fleet/vehicles",
				label: "Vehicle Monitor",
				icon: I.truck,
				permission: "fleet:read",
				view: () =>
					import("../features/new-dashboard/vehicle-monitor.ts").then(
						(m) => new m.VehicleMonitorView(),
					),
			},
			{
				id: "fleet/stations",
				label: "Stations Overview",
				icon: I.map,
				permission: "fleet:read",
				view: () =>
					import("../features/new-dashboard/stations-overview.ts").then(
						(m) => new m.StationsOverviewView(),
					),
			},
			{
				id: "fleet/alerts",
				label: "Alerts",
				icon: I.bell,
				alertBadge: true,
				permission: "fleet:read",
				view: () =>
					import("../features/fleet/alerts.ts").then((m) => new m.AlertsView()),
			},
			{
				id: "fleet/drivers",
				label: "Drivers",
				icon: I.users,
				permission: "fleet:read",
				view: () =>
					import("../features/new-dashboard/drivers.ts").then(
						(m) => new m.DriversView(),
					),
			},
			{
				id: "fleet/geofences",
				label: "Geofences",
				icon: I.fence,
				permission: "geofences:manage",
				view: () =>
					import("../features/admin/index.ts").then(
						(m) => new m.GeofencesView(),
					),
			},
			{
				id: "fleet/devices",
				label: "Device Monitoring",
				icon: I.activity,
				permission: "fleet:read",
				view: () =>
					import("../features/new-dashboard/device-monitoring.ts").then(
						(m) => new m.DeviceMonitoringView(),
					),
			},
			{
				id: "fleet/reports",
				label: "Fleet Analytics",
				icon: I.report,
				permission: "reports:view",
				view: () =>
					import("../features/analytics/reports.ts").then(
						(m) => new m.ReportsView(),
					),
			},
			{
				id: "fleet/platform-settings",
				label: "IOT Settings",
				icon: I.cog,
				permission: "settings:manage",
				view: () =>
					import("../features/new-dashboard/platform-settings.ts").then(
						(m) => new m.PlatformSettingsView(),
					),
			},
		],
	},
	{
		label: "Data",
		items: [
			{
				id: "data/telemetry",
				label: "Telemetry Log",
				icon: I.activity,
				permission: "fleet:read",
				view: () =>
					import("../features/data/telemetry-log.ts").then(
						(m) => new m.TelemetryLogView(),
					),
			},
		],
	},
	{
		label: "Shuttle",
		capability: "shuttle",
		items: [
			{
				id: "shuttle/registration",
				label: "Registration",
				icon: I.building,
				permission: "rides:manage",
				view: () =>
					import("../features/new-dashboard/registration.ts").then(
						(m) => new m.RegistrationView(),
					),
			},
			{
				id: "shuttle/dispatching",
				label: "Dispatching",
				icon: I.shuttle,
				permission: "rides:manage",
				view: () =>
					import("../features/new-dashboard/dispatching.ts").then(
						(m) => new m.DispatchingView(),
					),
			},
			{
				id: "shuttle/app-users",
				label: "App Users",
				icon: I.users,
				permission: "reports:view",
				view: () =>
					import("../features/new-dashboard/app-users.ts").then(
						(m) => new m.AppUsersView(),
					),
			},
			{
				id: "shuttle/admin",
				label: "Shuttle Admin",
				icon: I.shuttle,
				permission: "settings:manage",
				view: () =>
					import("../features/shuttle/admin.ts").then(
						(m) => new m.ShuttleAdminView(),
					),
			},
		],
	},
	{
		label: "User Settings",
		items: [
			{
				id: "user/preferences",
				label: "Preferences",
				icon: I.cog,
				view: () =>
					import("../features/new-dashboard/preferences.ts").then(
						(m) => new m.PreferencesView(),
					),
			},
			{
				id: "user/security",
				label: "Security",
				icon: I.lock,
				view: () =>
					import("../features/new-dashboard/security.ts").then(
						(m) => new m.SecurityView(),
					),
			},
		],
	},
	{
		label: "Administration",
		items: [
			{
				id: "admin/users",
				label: "Users & Roles",
				icon: I.users,
				permission: "users:manage",
				view: () =>
					import("../features/admin/index.ts").then((m) => new m.UsersView()),
			},
			{
				id: "admin/tenants",
				label: "Tenants",
				icon: I.building,
				permission: "settings:manage",
				view: () =>
					import("../features/admin/index.ts").then((m) => new m.TenantsView()),
			},
			{
				id: "admin/customizations",
				label: "Layout Customizer",
				icon: I.grid,
				superadminOnly: true,
				view: () =>
					import("../features/admin/customizations.ts").then(
						(m) => new m.CustomizationsView(),
					),
			},
		],
	},
	{
		label: "Developer",
		items: [
			{
				id: "dev/simulator",
				label: "Simulator",
				icon: I.play,
				dev: true,
				superadminOnly: true,
				view: () =>
					import("../features/dev/simulator.ts").then(
						(m) => new m.SimulatorView(),
					),
			},
		],
	},
];

/** Flatten the nav tree into a route-id → factory map for the router. */
export function buildRoutes(visible: NavSection[]): Map<string, ViewFactory> {
	const map = new Map<string, ViewFactory>();
	for (const section of visible) {
		for (const item of section.items) map.set(item.id, item.view);
	}
	return map;
}

/**
 * Filter the nav tree to what the given role + tenant capabilities may access.
 * A section tagged with a `capability` is hidden unless the tenant has it
 * enabled, so the platform shows only the capabilities a tenant is provisioned
 * for (e.g. a fleet-only tenant never sees the Shuttle section).
 */
export function visibleNav(
	role: string,
	permissions: string[] = [],
	capabilities: string[] = [],
): NavSection[] {
	const has = (cap?: string) => !cap || capabilities.includes(cap);
	const hasPerm = (item: NavItem) => {
		if (item.superadminOnly && role !== "superadmin") return false;
		if (role === "superadmin") return true;
		if (!item.permission) return true;
		return permissions.includes(item.permission);
	};
	return NAV.filter((section) => has(section.capability))
		.map((section) => ({
			label: section.label,
			capability: section.capability,
			items: section.items.filter(hasPerm),
		}))
		.filter((section) => section.items.length > 0);
}

/**
 * Whether one nav item is visible to this user, by the same rules visibleNav
 * applies.
 *
 * Exported so a page can ask about a page other than itself. The reports page
 * needs it: alert figures belong to whoever may open the Alerts page, and
 * hard-coding "fleet:read" there would be a second copy of a rule that already
 * lives on the nav item, free to drift from it.
 */
export function navItemVisible(
	id: string,
	role: string,
	permissions: string[] = [],
	capabilities: string[] = [],
): boolean {
	for (const section of visibleNav(role, permissions, capabilities)) {
		if (section.items.some((item) => item.id === id)) return true;
	}
	return false;
}

/** Resolve a nav item by route id (across all sections). */
export function findItem(id: string): NavItem | undefined {
	for (const s of NAV) {
		const it = s.items.find((x) => x.id === id);
		if (it) return it;
	}
	return undefined;
}
